import React from 'react'
import { clsx } from 'clsx'

/**
 * IconButton 组件的 props 接口
 */
interface IconButtonProps {
  /** 图标元素 */
  icon: React.ReactNode
  /** 按钮类型 */
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline'
  /** 按钮大小 */
  size?: 'sm' | 'md' | 'lg'
  /** 是否禁用 */
  disabled?: boolean
  /** 是否为加载状态 */
  loading?: boolean
  /** 按钮类型 */
  type?: 'button' | 'submit' | 'reset'
  /** 点击事件处理器 */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** 工具提示文本 */
  tooltip?: string
  /** 自定义类名 */
  className?: string
  /** 其他 HTML button 属性 */
  [key: string]: any
}

/**
 * 图标按钮组件
 * 专门用于显示图标的按钮，支持工具提示
 */
export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  variant = 'secondary',
  size = 'md',
  disabled = false,
  loading = false,
  type = 'button',
  onClick,
  tooltip,
  className,
  ...props
}) => {
  /**
   * 获取按钮样式对象（使用CSS变量）
   */
  const getStyles = () => {
    const baseStyles: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      outline: 'none',
      position: 'relative',
      overflow: 'hidden',
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      border: 'none',
      background: 'transparent',
    }

    // 大小样式
    const sizeStyles = {
      sm: { width: '32px', height: '32px', borderRadius: '6px' },
      md: { width: '40px', height: '40px', borderRadius: '8px' },
      lg: { width: '48px', height: '48px', borderRadius: '12px' },
    }

    // 变体样式
    const variantStyles = {
      primary: {
        background: 'var(--accent-blue)',
        color: 'white',
      },
      secondary: {
        background: 'var(--bg-hover)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-light)',
      },
      ghost: {
        background: 'transparent',
        color: 'var(--text-secondary)',
      },
      outline: {
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-light)',
      },
    }

    return {
      ...baseStyles,
      ...sizeStyles[size],
      ...variantStyles[variant],
    }
  }

  /**
   * 获取hover样式
   */
  const getHoverStyles = () => {
    const hoverStyles = {
      primary: { background: '#1a6dc7' },
      secondary: { background: 'var(--bg-active)' },
      ghost: { background: 'var(--bg-hover)' },
      outline: { background: 'var(--bg-hover)' },
    }
    return hoverStyles[variant]
  }


  /**
   * 获取按钮基础样式类名
   */
  const getBaseStyles = () => {
    return clsx(
      // 基础样式
      'focus:outline-none',
      
      // 加载状态
      {
        'cursor-wait': loading,
      },
      
      className
    )
  }

  /**
   * 渲染加载指示器
   */
  const renderLoader = () => {
    if (!loading) return null
    
    return (
      <svg
        className="animate-spin h-full w-full"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )
  }

  /**
   * 渲染图标
   */
  const renderIcon = () => {
    if (loading) return renderLoader()
    
    const iconElement = icon as React.ReactElement<any>
    return React.cloneElement(iconElement, {
      className: clsx(
        iconElement.props.className,
        {
          'w-4 h-4': size === 'sm',
          'w-5 h-5': size === 'md',
          'w-6 h-6': size === 'lg',
        }
      )
    })
  }

  /**
   * 处理点击事件
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      event.preventDefault()
      return
    }
    
    onClick?.(event)
  }

  const button = (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={handleClick}
      className={getBaseStyles()}
      style={getStyles()}
      title={tooltip}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.currentTarget.style, getHoverStyles())
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.currentTarget.style, getStyles())
        }
      }}
      {...props}
    >
      {renderIcon()}
    </button>
  )

  // 如果有工具提示，可以在这里扩展实现
  // 目前使用简单的 title 属性
  return button
}

/**
 * IconButton 组件的显示名称
 */
IconButton.displayName = 'IconButton'

/**
 * 导出 IconButtonProps 类型
 */
export type { IconButtonProps }